import React, { useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { User } from "lucide-react";
import type { WhatsAppConversationItem } from "../../../../Data/whatsapp.dto";

interface ConversationListProps {
  conversations: WhatsAppConversationItem[];
  activeId: string | null;
  onSelect: (conversation: WhatsAppConversationItem) => void;
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

const formatLastMessageTime = (dateStr?: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  if (isNaN(diffMs)) return "";

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return "just now";
  } else if (diffMins < 60) {
    return `${diffMins}m`;
  } else if (diffHours < 24) {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: true });
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: "short" });
  } else {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
};

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeId,
  onSelect,
  loading,
  hasMore,
  onLoadMore,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore || !onLoadMore || loading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      onLoadMore();
    }
  }, [hasMore, onLoadMore, loading]);

  if (loading && conversations.length === 0) {
    return (
      <ListContainer>
        {[1, 2, 3].map((i) => (
          <SkeletonItem key={i} />
        ))}
      </ListContainer>
    );
  }

  if (conversations.length === 0) {
    return (
      <ListContainer>
        <EmptyText>No conversations found</EmptyText>
        <EmptySub>New chats will appear here</EmptySub>
      </ListContainer>
    );
  }

  return (
    <ListContainer ref={listRef} onScroll={handleScroll}>
      {conversations.map((c) => {
        const title = c.customer?.name?.trim() || c.customer_number;
        const isWindowOpen =
          c.is_window_open !== undefined
            ? Boolean(c.is_window_open)
            : c.conversation_type === "user_session" && c.window_expiry
              ? new Date() < new Date(c.window_expiry)
              : false;
        const lastMsg = c.last_message;
        const lastMsgTime = c.last_message_at || c.updated_at || c.created_at;
        const timeStr = formatLastMessageTime(lastMsgTime);
        const hasUnread = c.unread_count != null && c.unread_count > 0;

        return (
          <ConversationItem
            key={c.id}
            $active={activeId === c.id}
            $hasUnread={hasUnread}
            onClick={() => onSelect(c)}
          >
            {/* Avatar */}
            <AvatarWrapper>
              <Avatar>
                <User size={20} />
              </Avatar>
              {/* Green/grey online dot on avatar */}
              <WindowIndicator
                $open={isWindowOpen}
                title={isWindowOpen ? "24h service window open" : "Window closed — template required"}
              />
            </AvatarWrapper>

            {/* Content */}
            <ItemContent>
              {/* Row 1: Name + Time */}
              <ItemHeaderRow>
                <ItemName $hasUnread={hasUnread}>{title}</ItemName>
                {timeStr && (
                  <TimeText $hasUnread={hasUnread}>{timeStr}</TimeText>
                )}
              </ItemHeaderRow>

              {/* Row 2: Last message preview + Unread badge */}
              <ItemBodyRow>
                <LastMessageText $hasUnread={hasUnread}>
                  {lastMsg != null
                    ? lastMsg || <em style={{ color: "#9ca3af", fontStyle: "normal" }}>No message</em>
                    : <em style={{ color: "#9ca3af", fontStyle: "normal" }}>Start a conversation</em>}
                </LastMessageText>
                {hasUnread && (
                  <UnreadBadge>
                    {c.unread_count! > 99 ? "99+" : c.unread_count}
                  </UnreadBadge>
                )}
              </ItemBodyRow>
            </ItemContent>
          </ConversationItem>
        );
      })}
      {loading && <SkeletonItem />}
    </ListContainer>
  );
};

export default ConversationList;

/* ── Styled Components ───────────────────────────────────────────────────── */

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const ConversationItem = styled.div<{ $active: boolean; $hasUnread: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  position: relative;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, rgba(255, 201, 81, 0.15) 0%, rgba(201, 77, 170, 0.15) 100%)"
      : "transparent"};
  border-left: 3px solid ${(p) => (p.$active ? "#C94DAA" : "transparent")};
  transition: background 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: ${(p) =>
      p.$active
        ? "linear-gradient(144.01deg, rgba(255, 201, 81, 0.22) 0%, rgba(201, 77, 170, 0.22) 100%)"
        : "rgba(0, 0, 0, 0.04)"};
  }

  /* Bottom divider */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 72px;
    right: 16px;
    height: 1px;
    background: rgba(0, 0, 0, 0.05);
  }
`;

/* Avatar with window indicator dot */
const AvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 700;
`;

const WindowIndicator = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(p) => (p.$open ? "#22c55e" : "#9ca3af")};
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px ${(p) => (p.$open ? "rgba(34,197,94,0.35)" : "transparent")};
  transition: background-color 0.3s ease;
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

/* Row 1: name + time */
const ItemHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ItemName = styled.div<{ $hasUnread: boolean }>`
  font-family: "Urbanist", sans-serif;
  font-size: 14.5px;
  font-weight: ${(p) => (p.$hasUnread ? 700 : 600)};
  color: ${(p) => (p.$hasUnread ? "#111827" : "#374151")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const TimeText = styled.div<{ $hasUnread: boolean }>`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: ${(p) => (p.$hasUnread ? 700 : 500)};
  color: ${(p) => (p.$hasUnread ? "#25D366" : "#9ca3af")};
  flex-shrink: 0;
  white-space: nowrap;
`;

/* Row 2: last message + unread badge */
const ItemBodyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const LastMessageText = styled.div<{ $hasUnread: boolean }>`
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: ${(p) => (p.$hasUnread ? 600 : 400)};
  color: ${(p) => (p.$hasUnread ? "#374151" : "#9ca3af")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const popIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;

const UnreadBadge = styled.div`
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 10px;
  background: #25D366;
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.2px;
  animation: ${popIn} 0.25s ease-out forwards;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.45);
`;

const EmptyText = styled.div`
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #6b7280;
  padding: 24px 16px;
  text-align: center;
`;

const EmptySub = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  padding: 0 16px 24px;
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const SkeletonItem = styled.div`
  height: 72px;
  margin: 6px 16px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;
